/* eslint-disable react/display-name */
import { useLocalState } from '@hooks/useLocalState'
import {
	Box,
	FormControl,
	FormHelperText,
	InputAdornment,
	TextField,
	TextFieldProps,
} from '@mui/material'
import React, { ChangeEvent, forwardRef, ReactNode } from 'react'
import { FieldError, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

type InputProps = TextFieldProps & {
	unit?: string
	width?: 'half' | 'full'
	fieldError?: FieldError
	name?: string
	endAdornment?: ReactNode
	validateOnBlur?: boolean
	type?: TextFieldProps['type'] | 'year'
}

const Input = forwardRef((props: InputProps, ref) => {
	const zeroPadding = { padding: 0 }
	const { t } = useTranslation()
	const {
		unit,
		onChange,
		fieldError,
		name,
		value,
		type,
		variant,
		endAdornment,
		validateOnBlur,
		'aria-labelledby': ariaLabelledBy,
		inputProps,
		...rest
	} = props
	const { trigger } = useFormContext()

	const isYearType = type === 'year'

	const [yearValue, setYearValue] = useLocalState<string>(
		isYearType && value ? String(value) : ''
	)

	const castFieldOnChange = (nextValue: unknown) => {
		if (!onChange) {
			return
		}
		;(onChange as unknown as (value: unknown) => void)(nextValue)
	}

	const onChangeAndRevalidate = (
		e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
	) => {
		if (isYearType) {
			setYearValue(e.target.value)
		}
		onChange?.(e)
		if (!validateOnBlur) {
			trigger(name)
		}
	}

	const onBlurHandler = (
		e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		if (isYearType) {
			const trimmedValue = e.target.value.trim()
			if (trimmedValue !== yearValue) {
				setYearValue(trimmedValue)
			}
			if (onChange) {
				if (trimmedValue === '') {
					castFieldOnChange(undefined)
				} else {
					const parsedValue = Number(trimmedValue)
					if (Number.isNaN(parsedValue)) {
						castFieldOnChange(trimmedValue)
					} else {
						castFieldOnChange(parsedValue)
					}
				}
			}
		}
		if (validateOnBlur) {
			trigger(name)
		}
		props.onBlur?.(e)
	}

	const numberInputSpecificProps =
		type === 'number'
			? {
					onWheel: (e: React.WheelEvent<HTMLInputElement>) => {
						// Prevent scroll from changing number value
						e.currentTarget.blur()
						inputProps?.onWheel?.(e)
					},
					onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
						// Prevent arrow up/down from changing number value
						if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
							e.preventDefault()
						}
						inputProps?.onKeyDown?.(e)
					},
				}
			: {}

	const messageRenderer = () => {
		switch (fieldError?.type) {
			case 'required':
				return t('input.required')
			case 'minLength':
				return t('input.minLength')
			case 'maxLength':
				return t('input.maxLength')
			case 'min':
			case 'pattern':
			case 'validate':
				return fieldError?.message
			default:
				return null
		}
	}

	const resolvedType = isYearType ? 'text' : type
	const resolvedInputProps = {
		'aria-labelledby': ariaLabelledBy,
		...inputProps,
		...(isYearType ? { inputMode: 'numeric' as const } : {}),
		...(resolvedType === 'number' ? { ...numberInputSpecificProps } : {}),
	}

	return (
		<Box ref={ref}>
			<FormControl required={rest.required} fullWidth>
				<TextField
					variant={variant}
					onChange={onChangeAndRevalidate}
					error={!!fieldError?.type}
					sx={zeroPadding}
					inputProps={resolvedInputProps}
					InputProps={{
						endAdornment: unit ? (
							<InputAdornment position="end">{unit}</InputAdornment>
						) : endAdornment ? (
							endAdornment
						) : null,
					}}
					InputLabelProps={{
						shrink: true,
					}}
					value={
						isYearType
							? yearValue
							: resolvedType === 'number' && value
								? (value as string | number).toString()
								: value
					}
					type={resolvedType}
					onBlur={onBlurHandler}
					{...rest}
				/>
				<Box height={6}>
					<FormHelperText error>{messageRenderer()}</FormHelperText>
				</Box>
			</FormControl>
		</Box>
	)
})

export { Input }
